import { useCallback } from 'react'
import { useSelector } from '.'
import { ApplicationDataFull } from '../../../types/ApplicationData'
import { ConstelaConfiguration } from '../../../types/ConstelaConfiguration'
import ModuleData, { isSingularModule } from '../../../types/ModuleData'
import { getCurrentModuleVariation } from '../../model/ModuleData'
import { useAvailableModules } from './dataHooks'
import { useCurrentApplication } from './uiHooks'

export const useCurrentConfiguration = (): ConstelaConfiguration => {
  const { id: applicationId = '' } = useCurrentApplication() ?? {}

  return useSelector((state) => {
    if (state.ui.mode === 'expert') {
      return state.configuration.expertMode
    }
    return state.configuration.inspirationMode[applicationId]
  })
}

export const getModuleHeight = (
  module: ModuleData | undefined,
  globalProperties: Record<string, string>,
  moduleProperties?: Record<string, string>
): number => {
  const _module = getCurrentModuleVariation(module, globalProperties, moduleProperties)
  return (_module && 'height' in _module ? _module?.height : 0) ?? 0
}

export const getSystemHeight = (modules: ModuleData[], configuration: ConstelaConfiguration): number => {
  return [configuration.lightingHead, ...configuration.modules, configuration.supportingColumn]
    .map(({ id, properties }) => {
      const moduleData = modules.find((module) => module.id === id)
      if (isSingularModule(moduleData)) {
        return 0
      }
      return getModuleHeight(moduleData, configuration.properties, properties)
    })
    .reduce((a, b) => a + b, 0)
}

export const useCanInsertModule = (
  application: ApplicationDataFull,
  configuration: ConstelaConfiguration
): ((moduleId?: string) => boolean) => {
  const maxHeight = useSelector((state) => state.data.system.maxHeight)
  const availableModules = useAvailableModules(application.id, configuration.type)

  return useCallback(
    (moduleId) => {
      if (!moduleId) {
        return false
      }
      const systemHeight = getSystemHeight(availableModules, {
        ...configuration,
        modules: configuration.modules.concat([{ id: moduleId }])
      })
      return systemHeight <= maxHeight
    },
    [availableModules, configuration, maxHeight]
  )
}
